import { useState, FC, useMemo } from 'react';

import { useAppDispatch } from '@/store/store';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { setSearchValue } from '@/store/slices/searchSlice';

import { useNavigate } from 'react-router';

import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

import debounce from 'debounce';

interface IInputSearch {
  className?: string;
}

/**
 * Search input component with real-time Redux state updates and navigation support.
 *
 * - Updates the search value in the Redux store in real-time as the user types.
 * - Automatically navigates to the '/search' route when typing starts (if not already there).
 * - Displays a clear ("X") button when there is input text, allowing users to clear the search field.
 *
 * @param props - Component properties.
 * @param props.className - Optional additional CSS classes for the container.
 */

const InputSearch: FC<IInputSearch> = ({ className }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const storedSearchValue = useSelector(
    (state: RootState) => state.search.searchValue
  );
  const [value, setValue] = useState<string>(storedSearchValue);

  const debouncedDispatch = useMemo(
    () =>
      debounce((val: string) => {
        dispatch(setSearchValue(val));
      }, 500),
    [dispatch]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    debouncedDispatch(newValue);

    if (newValue.trim() === '') {
      navigate('/');
    } else {
      navigate('/search');
    }
  };

  const clearInput = () => {
    setValue('');
    dispatch(setSearchValue(''));
    navigate('/');
  };

  return (
    <div className={`relative w-[296px] h-[33px] flex ${className ?? ''}`}>
      <div className="w-[33px] h-full bg-[#1F2B40] flex items-center justify-center rounded-l-xl">
        <MagnifyingGlassIcon className="size-4 text-white" />
      </div>
      <div className="relative w-full h-full">
        <input
          placeholder="Navn, dato eller beløb"
          type="text"
          value={value}
          onChange={handleChange}
          className="w-full h-full pl-3 pr-8 border border-[#1F2B40] rounded-r-xl bg-white text-[#1F2B40] placeholder:text-[#1F2B40CC/80] text-sm focus:outline-none focus:border-black"
        />
        {value && (
          <button
            type="button"
            onClick={clearInput}
            className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <XMarkIcon className="size-4 text-[#1F2B40] cursor-pointer" />
          </button>
        )}
      </div>
    </div>
  );
};

export default InputSearch;
